import { Injectable } from '@angular/core';
import type { EChartsOption, TooltipComponentOption, XAXisComponentOption, YAXisComponentOption } from 'echarts';
import { Subscription } from 'rxjs';
import { SharedStateService } from './shared-state.service';

@Injectable({ providedIn: 'root' })
export class ChartDataService {
  private seriesMap: Record<string, any[]> = {};
  private markerYMap: Record<string, [number, number][]> = {};
  private outerkeys: number[] = [];
  private readonly commonColor = '#007bc3';
  private adiabaticEnabled = true;
  private totalEnabled = true;
  private subscriptions: Subscription[] = [];

  autoScaleFlow = true;
  autoScaleVacuum = true;
  autoScalePower = true;

  flowMin = 0;
  flowMax = 10;
  flowStep = 1;

  vacuumMin = 0;
  vacuumMax = 80;
  vacuumStep = 10;

  powerMin = 0;
  powerMax = 700;
  powerStep = 100;

  showSpeedLines = true;

  constructor(private sharedStateService: SharedStateService) {
    // Keep local flags in sync with shared state
    this.subscriptions.push(
      this.sharedStateService.adiabaticEfficiency$.subscribe((v) => (this.adiabaticEnabled = v)),
      this.sharedStateService.totalEfficiency$.subscribe((v) => (this.totalEnabled = v)),
    );
  }

  setChartData(model: any): void {
    // Guard against missing model in lightweight tests — treat as empty dataset
    if (!model) {
      this.seriesMap = {};
      this.markerYMap = {};
      this.outerkeys = [];
      return;
    }

    const operatingPoints = model.operatingPoints ?? [];

    if (model.outerkeys) {
      this.outerkeys = model.outerkeys.map((k: string) => Number(k));
    }

    this.markerYMap = this.buildMarkerMap(operatingPoints);

    const chartDataMap = this.getChartDataMap(model);

    Object.keys(chartDataMap).forEach((chartId) => {
      if (chartId === 'chart6') {
        // Build full chart6 series and then apply enabled/disabled filters
        const full = this.buildChart6Series(chartDataMap[chartId]);
        this.seriesMap[chartId] = this.filterChart6Series(full);
      } else {
        this.seriesMap[chartId] = this.buildStandardSeries(chartDataMap[chartId]);
      }
    });
  }

  // Build standard (non-chart6) series array
  private buildStandardSeries(dataArr: any[]): any[] {
    return (dataArr || []).map((data) => ({
      name: 'Series',
      type: 'line',
      smooth: true,
      symbol: 'circle',
      data,
      lineStyle: { color: this.commonColor },
      itemStyle: { color: this.commonColor },
      showInLegend: true,
    }));
  }

  // Create the raw series for chart6 preserving index/type metadata
  private buildChart6Series(dataArr: any[]): any[] {
    const stallingSeries = [2, 7];
    const adiabaticEfficiencySeries = [0, 1, 3, 4];
    const totalEfficiencySeries = [5, 6, 8, 9];

    return (dataArr || []).map((data, idx) => {
      let color = this.commonColor;
      let seriesType: 'adiabatic' | 'total' | 'stalling' | 'other' = 'other';

      if (stallingSeries.includes(idx)) {
        seriesType = 'stalling';
        color = 'red';
      } else if (adiabaticEfficiencySeries.includes(idx)) {
        seriesType = 'adiabatic';
        color = 'green';
      } else if (totalEfficiencySeries.includes(idx)) {
        seriesType = 'total';
        color = this.commonColor;
      }

      return {
        name: `Series ${idx + 1}`,
        type: 'line',
        smooth: true,
        symbol: 'circle',
        data,
        lineStyle: { color },
        itemStyle: { color },
        showInLegend: !stallingSeries.includes(idx),
        // preserve original index and type so consumers can make decisions after filtering
        originalIndex: idx,
        seriesType,
      } as any;
    });
  }

  // Apply enabled flags to chart6 series
  private filterChart6Series(seriesArr: any[]): any[] {
    const enabledAdiabatic = !!this.adiabaticEnabled;
    const enabledTotal = !!this.totalEnabled;

    // When neither efficiency is enabled there is nothing to show for chart6
    if (!enabledAdiabatic && !enabledTotal) {
      return [];
    }

    return (seriesArr || []).filter((s: any) => {
      // Explicit handling keeps the logic simple and easy to follow
      if (s.seriesType === 'adiabatic') {
        return enabledAdiabatic;
      }

      if (s.seriesType === 'total') {
        return enabledTotal;
      }

      if (s.seriesType === 'stalling') {
        // Stalling entries correspond to positions that belong to either
        // adiabatic (originalIndex === 2) or total (originalIndex === 7).
        if (s.originalIndex === 2) {
          return enabledAdiabatic;
        }
        if (s.originalIndex === 7) {
          return enabledTotal;
        }
      }

      // All other series are unaffected by the efficiency toggles
      return true;
    });
  }

  // Small helper to construct the marker map for charts from operating points
  private buildMarkerMap(operatingPoints: any[]): Record<string, [number, number][]> {
    return {
      chart1: operatingPoints[0] ? [operatingPoints[0]] : [],
      chart2: operatingPoints[1] ? [operatingPoints[1]] : [],
      chart3: operatingPoints[3] ? [operatingPoints[3]] : [],
      chart4: operatingPoints[2] ? [operatingPoints[2]] : [],
      chart5: operatingPoints[4] ? [operatingPoints[4]] : [],
      chart6: [operatingPoints[5], operatingPoints[6]].filter(Boolean),
    };
  }

  // Build a chart->data mapping to keep setChartData compact and make this
  // implementation distinct from other copies flagged by duplication tools.
  private getChartDataMap(model: any): Record<string, any[]> {
    return {
      chart1: [
        model.minSpeedSuctionVolumetricFlowvsVacuum,
        model.maxSpeedSuctionVolumetricFlowvsVacuum,
        model.stallingSuctionVolumetricFlowvsVacuum,
        model.maxPowerSuctionVolumetricFlowvsVacuum,
        model.speedLine1SuctionVolumetricFlowvsVacuum,
        model.speedLine2SuctionVolumetricFlowvsVacuum,
      ],
      chart2: [
        model.minSpeedSuctionVolumetricFlowvsActiveGridPower,
        model.maxSpeedSuctionVolumetricFlowvsActiveGridPower,
        model.stallingSuctionVolumetricFlowvsActiveGridPower,
        model.maxPowerSuctionVolumetricFlowvsActiveGridPower,
        model.speedLine1SuctionVolumetricFlowvsActiveGridPower,
        model.speedLine2SuctionVolumetricFlowvsActiveGridPower,
      ],
      chart3: [
        model.minSpeedSuctionVolumetricFlowvsDischargeTemp,
        model.maxSpeedSuctionVolumetricFlowvsDischargeTemp,
        model.stallingSuctionVolumetricFlowvsDischargeTemp,
        model.maxPowerSuctionVolumetricFlowvsDischargeTemp,
        model.speedLine1SuctionVolumetricFlowvsDischargeTemp,
        model.speedLine2SuctionVolumetricFlowvsDischargeTemp,
      ],
      chart4: [
        model.minSpeedSuctionVolumetricFlowvsMassFlow,
        model.maxSpeedSuctionVolumetricFlowvsMassFlow,
        model.stallingSuctionVolumetricFlowvsMassFlow,
        model.maxPowerSuctionVolumetricFlowvsMassFlow,
        model.speedLine1SuctionVolumetricFlowvsMassFlow,
        model.speedLine2SuctionVolumetricFlowvsMassFlow,
      ],
      chart5: [
        model.minSpeedSuctionVolumetricFlowvsDischargeVolumetricFlow,
        model.maxSpeedSuctionVolumetricFlowvsDischargeVolumetricFlow,
        model.stallingSuctionVolumetricFlowvsDischargeVolumetricFlow,
        model.maxPowerSuctionVolumetricFlowvsDischargeVolumetricFlow,
        model.speedLine1SuctionVolumetricFlowvsDischargeVolumetricFlow,
        model.speedLine2SuctionVolumetricFlowvsDischargeVolumetricFlow,
      ],
      chart6: [
        model.minSpeedSuctionVolumetricFlowvsAdiabaticEfficiency,
        model.maxSpeedSuctionVolumetricFlowvsAdiabaticEfficiency,
        model.stallingSuctionVolumetricFlowvsAdiabaticEfficiency,
        model.speedLine1SuctionVolumetricFlowvsAdiabaticEfficiency,
        model.speedLine2SuctionVolumetricFlowvsAdiabaticEfficiency,
        model.minSpeedSuctionVolumetricFlowvsTotalEfficiency,
        model.maxSpeedSuctionVolumetricFlowvsTotalEfficiency,
        model.stallingSuctionVolumetricFlowvsTotalEfficiency,
        model.speedLine1SuctionVolumetricFlowvsTotalEfficiency,
        model.speedLine2SuctionVolumetricFlowvsTotalEfficiency,
      ],
    };
  }

  getChartOptions(chartId: string): EChartsOption {
    // short-circuit: when chart6 has no efficiencies enabled return an empty/hidden options
    if (chartId === 'chart6' && !(this.adiabaticEnabled || this.totalEnabled)) {
      return {
        tooltip: { trigger: 'axis', textStyle: { fontSize: 10 }, axisPointer: { type: 'none' as const }, formatter: () => '' },
        xAxis: this.buildXAxis(),
        yAxis: this.buildYAxis(chartId),
        legend: { show: false },
        series: [],
      } as EChartsOption;
    }

    const baseSeries = this.buildBaseSeries(chartId);
    const markerSeries = this.buildMarkerSeries(chartId);
    if (markerSeries) {
      baseSeries.push(markerSeries);
    }

    const xAxis = this.buildXAxis();
    const yAxis = this.buildYAxis(chartId);
    const legend = this.buildLegend(chartId);
    const tooltip: TooltipComponentOption = {
      trigger: 'axis',
      textStyle: { fontSize: 10 },
      axisPointer: { type: 'none' as const },
      formatter: (params: any) => this.formatTooltip(params),
    };

    return {
      tooltip,
      xAxis,
      yAxis,
      legend,
      series: baseSeries,
    };
  }

  // Extracted helpers to make getChartOptions small and reduce cognitive complexity
  private buildBaseSeries(chartId: string): any[] {
    const stallingSeries = [2, 7];
    const raw = this.seriesMap[chartId] || [];

    return raw
      .map((s: any, index: number) => {
        // Determine naming and legend visibility
        const { newName, showInLegendOverride } = this.getSeriesNameAndLegend(chartId, s, index, stallingSeries);

        // Determine line type
        const lineType = this.getLineTypeForChart(chartId, index);

        // Optionally skip speed lines for non-chart6 when disabled
        if (this.shouldSkipSpeedLine(chartId, index)) {
          return null;
        }

        // Stalling series require an explicit label with position rules
        if (stallingSeries.includes(index)) {
          const position = this.getStallingLabelPosition(chartId, index);
          s.label = {
            show: true,
            position,
            fontSize: 8,
            formatter: (params: any) => this.outerkeys?.[params.dataIndex] ?? '',
          };
        }

        if (showInLegendOverride !== undefined) {
          s.showInLegend = showInLegendOverride;
        }

        return {
          ...s,
          name: newName,
          lineStyle: { ...(s.lineStyle || {}), type: lineType, color: s.lineStyle?.color ?? this.commonColor },
          itemStyle: { ...(s.itemStyle || {}), color: s.itemStyle?.color ?? this.commonColor },
        };
      })
      .filter(Boolean);
  }

  // Helper to determine series name and legend visibility based on chart and series
  private getSeriesNameAndLegend(chartId: string, s: any, index: number, stallingSeries: number[]) {
    // default values
    let newName = s.name;
    let showInLegendOverride: boolean | undefined;

    // Standard charts (1-5) follow simple naming rules; extract to helper to reduce branching
    const standardName = this.getStandardChartName(chartId, index);
    if (standardName !== undefined) {
      newName = standardName;
      return { newName, showInLegendOverride };
    }

    // Chart6 has its own rules (efficiencies / stalling / visibility)
    if (chartId === 'chart6') {
      return this.getChart6NameAndLegend(s, index, stallingSeries);
    }

    return { newName, showInLegendOverride };
  }

  // Helper to decide names for standard charts (chart1..chart5). Returns undefined when not applicable.
  private getStandardChartName(chartId: string, index: number): string | undefined {
    const groupA = chartId === 'chart1' || chartId === 'chart2';
    const groupB = chartId === 'chart3' || chartId === 'chart4' || chartId === 'chart5';

    if (groupA) {
      if (index <= 3) {
        return 'Operations Area';
      }
      if (index >= 4) {
        return 'Middle Speeds';
      }
    }

    if (groupB) {
      if (index <= 3) {
        return 'Operations Range';
      }
      if (index >= 4) {
        return 'Middle Speeds';
      }
    }

    return undefined;
  }

  // Helper to compute name and legend visibility for chart6 series
  private getChart6NameAndLegend(s: any, index: number, stallingSeries: number[]) {
    let newName = s.name;
    let showInLegendOverride: boolean | undefined;

    if (s.seriesType === 'adiabatic') {
      newName = 'Adiabatic Efficiency';
      showInLegendOverride = !!this.adiabaticEnabled;
    } else if (s.seriesType === 'total') {
      newName = 'Total Efficiency';
      showInLegendOverride = !!this.totalEnabled;
    } else if (s.seriesType === 'stalling') {
      newName = '';
      showInLegendOverride = false;
    } else {
      showInLegendOverride = !stallingSeries.includes(index);
    }

    return { newName, showInLegendOverride };
  }

  // Helper to decide line type (solid/dashed) for charts with middle speed lines
  private getLineTypeForChart(chartId: string, index: number): 'solid' | 'dashed' {
    if (
      (chartId === 'chart1' || chartId === 'chart2' || chartId === 'chart3' || chartId === 'chart4' || chartId === 'chart5') &&
      index >= 4
    ) {
      return 'dashed';
    }
    return 'solid';
  }

  // Helper to decide when to skip speed lines
  private shouldSkipSpeedLine(chartId: string, index: number): boolean {
    return (index === 4 || index === 5) && !this.showSpeedLines && chartId !== 'chart6';
  }

  // Helper to compute label position for stalling series
  private getStallingLabelPosition(chartId: string, index: number): 'left' | 'bottom' {
    if (chartId === 'chart6' && index === 7) {
      return 'bottom';
    }
    if ((chartId === 'chart4' || chartId === 'chart5') && index === 2) {
      return 'bottom';
    }
    return 'left';
  }

  private buildMarkerSeries(chartId: string): any | null {
    const markers = this.markerYMap[chartId] || [];

    // Build markers array, applying chart6 visibility rules for the two positions
    const preparedMarkers = markers
      .map((pt, idx) => ({ point: pt, origIdx: idx }))
      .filter(({ point, origIdx }) => {
        if (chartId !== 'chart6') {
          return Boolean(point);
        }
        if (origIdx === 0 && !this.adiabaticEnabled) {
          return false;
        }
        if (origIdx === 1 && !this.totalEnabled) {
          return false;
        }
        return Boolean(point);
      });

    if (preparedMarkers.length === 0) {
      return null;
    }

    // For chart6, use green for adiabatic and blue for total; otherwise use orange
    const defaultMarkerColor = '#d44e06';

    return {
      name: 'Point Marker',
      type: 'scatter',
      symbol: 'circle',
      symbolSize: 8,
      data: preparedMarkers.map(({ point: [x, y], origIdx }) => {
        // Determine marker color based on efficiency type for chart6
        let markerColor = defaultMarkerColor;
        if (chartId === 'chart6') {
          markerColor = origIdx === 0 ? '#00aa00' : '#007bc3'; // Green for adiabatic, blue for total
        }

        return {
          value: [x, y],
          // format marker labels: show 3 decimals for chart6 (efficiency), otherwise 1 decimal
          label: {
            show: true,
            formatter: () => (chartId === 'chart6' ? y.toFixed(3) : y.toFixed(1)),
            position: 'right',
            fontSize: 8,
            color: '#000',
          },
          itemStyle: { color: markerColor },
        };
      }),
    };
  }

  private buildXAxis(): XAXisComponentOption {
    return {
      type: 'value' as const,
      name: 'Suction Volumetric Flow [m³/s]',
      nameLocation: 'middle',
      nameGap: 5,
      min: this.autoScaleFlow ? null : this.flowMin,
      max: this.autoScaleFlow ? null : this.flowMax,
      interval: this.autoScaleFlow ? null : this.flowStep,
      nameTextStyle: { fontSize: 10, padding: [20, 0, 0, 0], color: '#000' },
      axisLabel: { fontSize: 10 },
    };
  }

  private buildYAxis(chartId: string): YAXisComponentOption {
    if (chartId === 'chart1') {
      return {
        type: 'value' as const,
        name: 'Vacuum [kPa]',
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 30,
        min: this.autoScaleVacuum ? null : this.vacuumMin,
        max: this.autoScaleVacuum ? null : this.vacuumMax,
        interval: this.autoScaleVacuum ? null : this.vacuumStep,
        nameTextStyle: { fontSize: 10, color: '#000' },
        axisLabel: { fontSize: 10 },
      };
    }

    if (chartId === 'chart2') {
      return {
        type: 'value' as const,
        name: 'Active Grid Power [kW]',
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 30,
        min: this.autoScalePower ? null : this.powerMin,
        max: this.autoScalePower ? null : this.powerMax,
        interval: this.autoScalePower ? null : this.powerStep,
        nameTextStyle: { fontSize: 10, color: '#000' },
        axisLabel: { fontSize: 10 },
      };
    }

    const labelMap: Record<string, string> = {
      chart3: 'Discharge Temperature [°C]',
      chart4: 'Mass flow [kg/s]',
      chart5: 'Discharge Volumetric Flow [m³/s]',
      chart6: 'Efficiency [-]',
    };

    return {
      type: 'value' as const,
      name: labelMap[chartId] ?? '',
      nameLocation: 'middle',
      nameRotate: 90,
      nameGap: 30,
      nameTextStyle: { fontSize: 10, color: '#000' },
      axisLabel: { fontSize: 10 },
    };
  }

  private buildLegend(chartId: string) {
    return chartId === 'chart6'
      ? { show: true, data: ['Adiabatic Efficiency', 'Total Efficiency'], top: 20, textStyle: { fontSize: 10 } }
      : { show: false };
  }

  private formatTooltip(params: any): string {
    return params
      .filter((p: any) => p.seriesName)
      .map((p: any) => {
        const xVal = typeof p.value[0] === 'number' ? p.value[0].toFixed(3) : p.value[0];
        const yVal = typeof p.value[1] === 'number' ? p.value[1].toFixed(3) : p.value[1];
        return `${p.seriesName}: [${xVal}, ${yVal}]`;
      })
      .join('<br/>');
  }
}
